import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { Pencil, Share2, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'
import { useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { AssignmentDialog } from '@/components/AssignmentDialog'
import { ConfirmButton } from '@/components/ConfirmButton'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Input } from '@/components/elements/input'
import { Layout } from '@/components/Layout'
import {
  deleteOrganizationEAPTemplate,
  getOrganizationEAPAssignment,
  getOrganizationEAPTemplates,
  setOrganizationEAPAssignment,
} from '@/services/organization.service'
import { toast } from '@/utils/helpers'

const assignmentLabel = (row) => {
  if (row.assignedToAll) return 'All facilities'
  const count = row.assignedFacilityCount ?? 0
  if (count > 0) return `${count} ${count === 1 ? 'facility' : 'facilities'}`
  return 'Not assigned'
}

const OrganizationEAPTemplates = observer(() => {
  const { organizationId } = useParams()
  const navigate = useNavigate()

  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sorting, setSorting] = useState([])
  const [pagination, setPagination] = useState({ pageSize: 20, pageIndex: 0 })
  const [assignEAP, setAssignEAP] = useState(null)

  const queryClient = useQueryClient()
  const queryKey = ['organization-eap-templates', organizationId, sorting, pagination, searchTerm]

  const { data: templates, isPending } = useQuery({
    queryKey,
    queryFn: () =>
      getOrganizationEAPTemplates({ organizationId, sorting, pagination, searchTerm }),
    placeholderData: keepPreviousData,
    enabled: !!organizationId,
  })

  const { isPending: pendingDelete, mutate: deleteTemplate } = useMutation({
    mutationFn: (id) => deleteOrganizationEAPTemplate({ organizationId, id }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast('EAP template deleted', 'success')
      queryClient.invalidateQueries({ queryKey: ['organization-eap-templates', organizationId] })
    },
  })

  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current

  const editPath = (id) => `/organizations/${organizationId}/eap-templates/${id}`

  return (
    <Layout breadcrumbs={['Organization', 'EAPs']}>
      <Layout.Heading title="EAPs" />
      <p className="text-muted-foreground mb-3 max-w-3xl text-sm">
        Build Emergency Action Plans and assign them to facilities. A shared EAP stays here as the
        single source — assigned facilities see it read-only for their members, and your edits
        propagate automatically.
      </p>

      <div className="flex w-full flex-row items-center justify-between">
        <Input
          className="w-full md:w-64"
          placeholder="Search EAPs"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            debounceSearch(e.target.value)
          }}
        />
        <Button onClick={() => navigate(editPath('new'))}>Add Shared EAP</Button>
      </div>

      <div className="mt-3">
        <DataTable
          columns={[
            {
              accessorKey: 'name',
              id: 'name',
              header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
              cell: ({ row }) => (
                <button
                  type="button"
                  className="text-action cursor-pointer text-left hover:underline"
                  onClick={() => navigate(editPath(row.original.id))}
                >
                  {row.original.name}
                </button>
              ),
            },
            {
              accessorKey: 'description',
              header: 'Description',
              cell: ({ row }) => (
                <span className="line-clamp-2 max-w-md text-sm">
                  {row.original.description || '—'}
                </span>
              ),
            },
            {
              id: 'questions',
              header: 'Questions',
              size: 110,
              cell: ({ row }) => row.original.questions?.length ?? 0,
            },
            {
              id: 'quiz',
              header: 'Quiz',
              size: 110,
              cell: ({ row }) =>
                row.original.quizEnabled ? (
                  <Badge variant="success">Enabled</Badge>
                ) : (
                  <span className="text-muted-foreground">—</span>
                ),
            },
            {
              id: 'assigned',
              header: 'Assigned to',
              size: 140,
              cell: ({ row }) => {
                const assigned =
                  row.original.assignedToAll || row.original.assignedFacilityCount > 0
                return (
                  <span className={assigned ? '' : 'text-muted-foreground'}>
                    {assignmentLabel(row.original)}
                  </span>
                )
              },
            },
            {
              id: 'actions',
              header: '',
              size: 160,
              cell: ({ row }) => (
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Assign to facilities"
                    aria-label="Assign to facilities"
                    onClick={() => setAssignEAP(row.original)}
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Edit"
                    aria-label="Edit template"
                    onClick={() => navigate(editPath(row.original.id))}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <ConfirmButton
                    variant="ghost"
                    size="sm"
                    title="Delete"
                    aria-label="Delete template"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    confirmContent={`Delete the shared EAP "${row.original.name}"? It will be removed from every facility it's assigned to.`}
                    confirmTitle="Delete Shared EAP"
                    loading={pendingDelete}
                    onClick={() => deleteTemplate(row.original.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </ConfirmButton>
                </div>
              ),
            },
          ]}
          data={templates?.results || []}
          loading={isPending}
          pagination={pagination}
          rowCount={templates?.count || 0}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
        />
      </div>

      {assignEAP && (
        <AssignmentDialog
          open
          setOpen={(v) => {
            if (!v) setAssignEAP(null)
          }}
          organizationId={organizationId}
          title={`Assign "${assignEAP.name}"`}
          fetchAssignment={() =>
            getOrganizationEAPAssignment({ organizationId, eapId: assignEAP.id })
          }
          saveAssignment={(payload) =>
            setOrganizationEAPAssignment({ organizationId, eapId: assignEAP.id, ...payload })
          }
          onSaved={() =>
            queryClient.invalidateQueries({
              queryKey: ['organization-eap-templates', organizationId],
            })
          }
        />
      )}
    </Layout>
  )
})

export default OrganizationEAPTemplates
